// ReviewSubmit.js — v8.0 (Structured Review + Strong/Needs Work summary)
// ---------------------------------------------------------------------
// ✅ Final submit still calls saveToMercury(true) (Mercury Connect)
// ✅ Cleaner structure for purchase vs refinance
// ✅ Shows application strength / completeness
// ✅ Highlights what is strong and what still needs work
// ✅ Prevents double-submit + disables buttons while saving
// ✅ Scrolls to top at start + after result

import React, { useMemo, useState } from "react"

function ReviewSubmit({ onBack, crmData, saveToMercury }) {
  const [loading, setLoading] = useState(false)
  const [successMsg, setSuccessMsg] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [refMsg, setRefMsg] = useState("")

  const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")
  const toNumber = (v) => {
    const raw = digitsOnly(v)
    return raw ? Number(raw) : 0
  }

  const formatMoney = (n) => {
    const num = Number(n || 0)
    if (!Number.isFinite(num) || num <= 0) return "—"
    return "$" + num.toLocaleString("en-AU")
  }

  const formatPct = (n) => {
    const num = Number(n)
    if (!Number.isFinite(num) || num <= 0) return "—"
    return `${num.toFixed(2)}%`
  }

  const summary = useMemo(() => {
    const d = crmData || {}

    const applicants =
      Array.isArray(d.applicants) && d.applicants.length
        ? d.applicants
        : Array.isArray(d.applicantDetails) && d.applicantDetails.length
          ? d.applicantDetails
          : []

    const nameOf = (a) => {
      const first = a?.firstName || a?.givenName || a?.fname || ""
      const last = a?.lastName || a?.surname || a?.lname || ""
      const full = `${first} ${last}`.trim()
      return full || a?.name || ""
    }

    const applicant1 = applicants[0] ? nameOf(applicants[0]) : ""
    const applicant2 = applicants[1] ? nameOf(applicants[1]) : ""
    const applicantCount = [applicant1, applicant2].filter(Boolean).length || applicants.length || 0

    const property = d?.property || d?.propertyDetails || {}
    const objectives = d?.objectives?.needsObjectives?.byApplicant || d?.objectives?.needsObjectives || d?.needsObjectives?.byApplicant || {}
    const incomeRows = Array.isArray(d?.income) ? d.income : []
    const assetsLiabilities = d?.assetsLiabilities || { assets: [], liabilities: [] }
    const assets = Array.isArray(assetsLiabilities?.assets) ? assetsLiabilities.assets : []
    const liabilities = Array.isArray(assetsLiabilities?.liabilities) ? assetsLiabilities.liabilities : []

    const purposeRaw =
      property?.applicationType ||
      d?.applicationType ||
      d?.loanPurpose ||
      d?.purpose ||
      d?.dealType ||
      d?.transactionType ||
      ""

    const purpose = String(purposeRaw || "").toLowerCase()
    const isPurchase = purpose.includes("purchase") || purpose.includes("buy")
    const isRefinance = purpose.includes("refi")

    const purposeLabel = isPurchase
      ? "Buying a property"
      : isRefinance
        ? "Refinancing"
        : purposeRaw || "Not selected"

    const propertyLine =
      property?.propertyAddress?.formatted ||
      property?.address ||
      property?.fullAddress ||
      [property?.street, property?.suburb, property?.state, property?.postcode].filter(Boolean).join(", ") ||
      ""

    const occupancyRaw = property?.occupancy || property?.propertyUse || property?.use || ""
    const occupancy = (() => {
      const s = String(occupancyRaw || "").toLowerCase()
      if (!s) return ""
      if (s.includes("invest")) return "Investment"
      if (s.includes("live")) return "Owner occupied"
      return occupancyRaw
    })()

    const purchasePrice = toNumber(property?.purchasePrice)
    const depositAmount = toNumber(property?.depositAmount)
    const purchaseLoanAmount = purchasePrice > 0 ? Math.max(0, purchasePrice - depositAmount) : 0
    const depositPct = purchasePrice > 0 ? (depositAmount / purchasePrice) * 100 : 0
    const purchaseLvr = purchasePrice > 0 ? (purchaseLoanAmount / purchasePrice) * 100 : 0

    const propertyValue = toNumber(property?.propertyValue)
    const loanBalance = toNumber(property?.loanBalance)
    const refinanceLvr = propertyValue > 0 ? (loanBalance / propertyValue) * 100 : 0
    const currentLender = property?.currentLender || property?.lender || ""

    const totalIncomeAnnual = incomeRows.reduce((sum, row) => {
      const amount = toNumber(row?.amount)
      const freq = String(row?.frequency || "").toLowerCase()

      if (!amount) return sum
      if (freq === "annual") return sum + amount
      if (freq === "monthly") return sum + amount * 12
      if (freq === "fortnightly") return sum + amount * 26
      if (freq === "weekly") return sum + amount * 52
      return sum + amount
    }, 0)

    const totalAssets = assets.reduce((sum, a) => sum + toNumber(a?.value), 0)
    const totalLiabilities = liabilities.reduce((sum, l) => sum + toNumber(l?.balance || l?.limit), 0)

    const strengths = []
    const needsWork = []

    if (applicant1) strengths.push("Main applicant details are completed.")
    else needsWork.push("Applicant details still need to be completed.")

    if (applicantCount >= 2) strengths.push("Two applicants have been captured.")
    if (propertyLine) strengths.push("Property address has been provided.")
    else needsWork.push("Property address is still missing.")

    if (isPurchase) {
      if (purchasePrice > 0) strengths.push("Purchase price has been entered.")
      else needsWork.push("Purchase price is still missing.")

      if (depositAmount > 0) strengths.push("Deposit amount has been captured.")
      else needsWork.push("Deposit amount is still missing.")

      if (depositPct >= 20) strengths.push("Deposit is strong at 20% or more.")
      else if (depositPct >= 5) strengths.push("Deposit has been provided and is within standard purchase ranges.")
      else if (purchasePrice > 0) needsWork.push("Deposit looks low or incomplete for the purchase.")

      if (purchaseLvr > 0 && purchaseLvr <= 80) strengths.push("LVR is strong at 80% or below.")
      else if (purchaseLvr > 95) needsWork.push("LVR appears high and may limit lender options.")
    }

    if (isRefinance) {
      if (propertyValue > 0) strengths.push("Estimated property value has been provided.")
      else needsWork.push("Estimated property value is still missing.")

      if (loanBalance > 0) strengths.push("Current loan balance has been entered.")
      else needsWork.push("Current loan balance is still missing.")

      if (currentLender) strengths.push("Current lender details have been captured.")

      if (refinanceLvr > 0 && refinanceLvr <= 80) strengths.push("Refinance LVR is in a strong range.")
      else if (refinanceLvr > 90) needsWork.push("Refinance LVR appears high and may affect options.")
    }

    if (incomeRows.length > 0 && totalIncomeAnnual > 0) strengths.push("Income has been captured.")
    else needsWork.push("Income details still need to be completed.")

    if (assets.length > 0) strengths.push("Assets have been entered.")
    if (liabilities.length > 0) strengths.push("Liabilities have been entered.")

    if (!assets.length && !liabilities.length) {
      needsWork.push("Assets and liabilities section looks incomplete.")
    }

    const objectiveA1 = objectives?.[1]?.objectives || objectives?.applicant1?.objectives || ""
    if (objectiveA1) strengths.push("Needs and objectives have been documented.")
    else needsWork.push("Needs and objectives section still needs to be completed.")

    let score = 0
    if (applicant1) score += 1
    if (propertyLine) score += 1
    if (incomeRows.length > 0 && totalIncomeAnnual > 0) score += 1
    if (assets.length > 0 || liabilities.length > 0) score += 1
    if (objectiveA1) score += 1

    if (isPurchase) {
      if (purchasePrice > 0) score += 1
      if (depositAmount > 0) score += 1
    }

    if (isRefinance) {
      if (propertyValue > 0) score += 1
      if (loanBalance > 0) score += 1
    }

    const strengthLabel =
      score >= 7 ? "Strong application" : score >= 5 ? "Good application" : score >= 3 ? "Getting there" : "Needs more detail"

    const strengthTone =
      score >= 7 ? "strong" : score >= 5 ? "good" : score >= 3 ? "medium" : "weak"

    return {
      applicant1,
      applicant2,
      applicantCount,
      purposeLabel,
      isPurchase,
      isRefinance,
      occupancy,
      propertyLine,
      purchasePrice,
      depositAmount,
      purchaseLoanAmount,
      depositPct,
      purchaseLvr,
      propertyValue,
      loanBalance,
      refinanceLvr,
      currentLender,
      totalIncomeAnnual,
      totalAssets,
      totalLiabilities,
      strengths,
      needsWork,
      strengthLabel,
      strengthTone,
      assetsCount: assets.length,
      liabilitiesCount: liabilities.length,
    }
  }, [crmData])

  const handleSubmit = async () => {
    if (loading) return

    setLoading(true)
    setSuccessMsg("")
    setErrorMsg("")
    setRefMsg("")

    try {
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch {}

    try {
      const resp = await saveToMercury(true)

      if (resp?.success) {
        setSuccessMsg(
          resp?.message || "All done — your application has been saved to Mercury Connect."
        )

        const ref =
          resp?.partnerReference ||
          resp?.opportunityId ||
          resp?.contactId ||
          resp?.referenceId ||
          ""

        if (ref) setRefMsg(`Reference: ${ref}`)
      } else {
        setErrorMsg(resp?.message || "We couldn’t save this to Mercury Connect. Please try again.")
      }
    } catch {
      setErrorMsg("We couldn’t save this to Mercury Connect. Please try again.")
    }

    setLoading(false)

    try {
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch {}
  }

  const toneStyles = {
    strong: { bg: "#E9FFF0", border: "#86D9A1", text: "#166534" },
    good: { bg: "#EAFBFF", border: "#4EDCF6", text: "#0F3D46" },
    medium: { bg: "#FFF9E8", border: "#F3C969", text: "#7C5800" },
    weak: { bg: "#FFF1F2", border: "#F2A3AD", text: "#8A1D2C" },
  }

  const tone = toneStyles[summary.strengthTone] || toneStyles.good

  const sectionCard = {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  }

  const summaryCard = {
    background: "#E6FBFF",
    border: "2px solid #4EDCF6",
    borderRadius: 16,
    padding: 18,
    marginBottom: 16,
  }

  const statusCard = {
    background: tone.bg,
    border: `1px solid ${tone.border}`,
    borderRadius: 14,
    padding: 14,
    marginBottom: 16,
    color: tone.text,
  }

  const grid2 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  }

  const label = { fontSize: 13, opacity: 0.72, marginBottom: 6, fontWeight: 600 }
  const value = { fontSize: 16, fontWeight: 800, color: "#141414" }

  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid rgba(20,20,20,0.10)",
    fontSize: 13,
    fontWeight: 700,
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "8px 0 12px" }}>
      <h2 style={{ marginBottom: 14 }}>Review &amp; Submit</h2>

      {successMsg && (
        <div
          style={{
            padding: 14,
            background: "#E9FFF0",
            border: "1px solid #86D9A1",
            borderRadius: 12,
            marginBottom: 14,
            fontWeight: 700,
          }}
        >
          <div>{successMsg}</div>
          {refMsg && <div style={{ marginTop: 6 }}>{refMsg}</div>}
        </div>
      )}

      {errorMsg && (
        <div
          style={{
            padding: 14,
            background: "#FFF1F2",
            border: "1px solid #F2A3AD",
            borderRadius: 12,
            marginBottom: 14,
            fontWeight: 700,
            color: "#8A1D2C",
          }}
        >
          {errorMsg}
        </div>
      )}

      <div style={statusCard}>
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 6 }}>
          {summary.strengthLabel}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          This gives you and the broker a quick view of how complete the application looks before submitting.
        </div>
      </div>

      <div style={summaryCard}>
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 14 }}>Application Summary</div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={pill}>{summary.purposeLabel}</div>
          {summary.occupancy ? <div style={pill}>{summary.occupancy}</div> : null}
          <div style={pill}>{summary.applicantCount || 1} applicant{summary.applicantCount === 1 ? "" : "s"}</div>
        </div>

        <div style={grid2}>
          <div>
            <div style={label}>Applicant 1</div>
            <div style={value}>{summary.applicant1 || "Not provided yet"}</div>
          </div>

          <div>
            <div style={label}>Applicant 2</div>
            <div style={value}>{summary.applicant2 || "—"}</div>
          </div>

          <div>
            <div style={label}>Property</div>
            <div style={value}>{summary.propertyLine || "Not provided yet"}</div>
          </div>

          <div>
            <div style={label}>Total income captured</div>
            <div style={value}>{formatMoney(summary.totalIncomeAnnual)}</div>
          </div>
        </div>
      </div>

      {summary.isPurchase && (
        <div style={sectionCard}>
          <div style={{ fontSize: 17, fontWeight: 900, marginBottom: 14 }}>Buying Summary</div>

          <div style={grid2}>
            <div>
              <div style={label}>Purchase price</div>
              <div style={value}>{formatMoney(summary.purchasePrice)}</div>
            </div>

            <div>
              <div style={label}>Deposit amount</div>
              <div style={value}>{formatMoney(summary.depositAmount)}</div>
            </div>

            <div>
              <div style={label}>Estimated loan amount</div>
              <div style={value}>{formatMoney(summary.purchaseLoanAmount)}</div>
            </div>

            <div>
              <div style={label}>Deposit percentage</div>
              <div style={value}>{formatPct(summary.depositPct)}</div>
            </div>

            <div>
              <div style={label}>Estimated LVR</div>
              <div style={value}>{formatPct(summary.purchaseLvr)}</div>
            </div>
          </div>
        </div>
      )}

      {summary.isRefinance && (
        <div style={sectionCard}>
          <div style={{ fontSize: 17, fontWeight: 900, marginBottom: 14 }}>Refinance Summary</div>

          <div style={grid2}>
            <div>
              <div style={label}>Estimated property value</div>
              <div style={value}>{formatMoney(summary.propertyValue)}</div>
            </div>

            <div>
              <div style={label}>Current loan balance</div>
              <div style={value}>{formatMoney(summary.loanBalance)}</div>
            </div>

            <div>
              <div style={label}>Estimated LVR</div>
              <div style={value}>{formatPct(summary.refinanceLvr)}</div>
            </div>

            <div>
              <div style={label}>Current lender</div>
              <div style={value}>{summary.currentLender || "Not provided yet"}</div>
            </div>
          </div>
        </div>
      )}

      <div style={sectionCard}>
        <div style={{ fontSize: 17, fontWeight: 900, marginBottom: 14 }}>Financial Snapshot</div>

        <div style={grid2}>
          <div>
            <div style={label}>Assets captured</div>
            <div style={value}>{summary.assetsCount}</div>
          </div>

          <div>
            <div style={label}>Liabilities captured</div>
            <div style={value}>{summary.liabilitiesCount}</div>
          </div>

          <div>
            <div style={label}>Estimated total assets</div>
            <div style={value}>{formatMoney(summary.totalAssets)}</div>
          </div>

          <div>
            <div style={label}>Estimated total liabilities</div>
            <div style={value}>{formatMoney(summary.totalLiabilities)}</div>
          </div>
        </div>
      </div>

      <div style={grid2}>
        <div
          style={{
            ...sectionCard,
            background: "#F8FFFB",
            border: "1px solid #CDEED9",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>What looks strong</div>
          {summary.strengths.length ? (
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              {summary.strengths.slice(0, 6).map((item, idx) => (
                <li key={idx} style={{ fontWeight: 600 }}>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontWeight: 600, opacity: 0.8 }}>No strengths identified yet.</div>
          )}
        </div>

        <div
          style={{
            ...sectionCard,
            background: "#FFF9F2",
            border: "1px solid #F3DEC2",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Still needs work</div>
          {summary.needsWork.length ? (
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              {summary.needsWork.slice(0, 6).map((item, idx) => (
                <li key={idx} style={{ fontWeight: 600 }}>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontWeight: 600, opacity: 0.8 }}>Nothing obvious missing.</div>
          )}
        </div>
      </div>

      <div
        style={{
          ...sectionCard,
          background: "#FAFAFA",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 8 }}>Before you submit</div>
        <div style={{ fontSize: 14, lineHeight: 1.65, fontWeight: 600, color: "#374151" }}>
          Once submitted, the application will be saved to <strong>Mercury Connect</strong> so the
          broker can review the file, confirm the structure, and move to next steps.
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <button
          onClick={onBack}
          disabled={loading}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: "#f2f2f2",
            cursor: loading ? "not-allowed" : "pointer",
            marginRight: 12,
            opacity: loading ? 0.6 : 1,
            fontWeight: 700,
          }}
        >
          ⬅ Back
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            background: "#4EDCF6",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 800,
            opacity: loading ? 0.6 : 1,
            color: "#141414",
          }}
        >
          {loading ? "Saving to Mercury Connect…" : "Submit & Save to Mercury Connect"}
        </button>
      </div>
    </div>
  )
}

export default ReviewSubmit